import { updateAdminUserApi, useAdminUser } from "@/api/services/admin/user";
import { TGetUserDto } from "@/api/services/admin/user/dtos/get-user.dto";
import { useMe } from "@/api/services/admin/auth";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Select } from "@/components/elements/Select";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { adminUserSchema } from "@/features/admin-user/schema/adminUserSchema";
import { TAdminUserForm } from "@/features/admin-user/types/adminUserForm";
import { convertUserDetailToForm } from "@/features/admin-user/utils/converts";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { isNumber } from "@/utils/number";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Grid, Stack, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

type TAdminUserEditFormProps = {
  adminUserData: TGetUserDto;
  isEditingOwnProfile: boolean;
};

const AdminUserEditForm: FC<TAdminUserEditFormProps> = ({ adminUserData, isEditingOwnProfile }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const methods = useForm<TAdminUserForm>({
    defaultValues: convertUserDetailToForm(adminUserData),
    resolver: yupResolver(adminUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control } = methods;

  const onSubmit = async (data: TAdminUserForm) => {
    try {
      if (id) {
        const rs = await updateAdminUserApi(id, data);
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_DETAIL, id] });
        navigate(getDynamicRoute(AppRoutes.adminUserDetail, { id }));
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await methods.handleSubmit(onSubmit)();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={adminUserSchema}>
      <AdminUserEditWrapper sx={{ mt: 1 }}>
        <Box sx={{ mb: 2.5 }}>
          <TopPage
            title="irohana担当者の編集"
            description="顧客側画面にログインできるユーザーアカウントに関する情報を編集できます。"
            backUrl={getDynamicRoute(AppRoutes.adminUserDetail, { id: id! })}
            enablePageBack={false}
          />
          <Box mt="36px">
            <MenuNavigate
              contentWidth="900px"
              contents={[
                {
                  title: "ユーザーの状態",
                  content: (
                    <InnerBox title="ユーザーの状態" padding={3}>
                      <Grid container columnSpacing={3} rowSpacing={2}>
                        <Grid item xs={6}>
                          <FormField control={control} name="activeStatus" label="ステータス" required>
                            <Select disabled={isEditingOwnProfile} enumName="EUserActiveStatus" />
                          </FormField>
                        </Grid>
                      </Grid>
                    </InnerBox>
                  ),
                },
                {
                  title: "基本情報",
                  content: (
                    <InnerBox title="基本情報" padding={3}>
                      <Grid container columnSpacing={3} rowSpacing={2}>
                        <Grid item xs={6}>
                          <FormField control={control} name="fullName">
                            <Input />
                          </FormField>
                        </Grid>
                        <Grid item xs={6}>
                          <FormField control={control} name="email">
                            <Input />
                          </FormField>
                        </Grid>
                        <Grid item xs={6}>
                          <FormField control={control} name="adminRole">
                            <Select enumName="EAdminRole" />
                          </FormField>
                        </Grid>
                      </Grid>
                    </InnerBox>
                  ),
                },
              ]}
              contentAffix={
                <FooterContainer sx={{ mt: 4 }}>
                  <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                    <Button size="sm-round" variant="secondary" component={Link} to={getDynamicRoute(AppRoutes.adminUserDetail, { id: id! })}>
                      戻る
                    </Button>
                    <Stack direction="row" gap={3}>
                      <Button disabled={loading} size="sm-round" onClick={handleSubmit} startIcon={<SaveIcon />}>
                        保存
                      </Button>
                    </Stack>
                  </Stack>
                </FooterContainer>
              }
            />
          </Box>
        </Box>
      </AdminUserEditWrapper>
    </Form>
  );
};

export const AdminUserEditPage: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: adminUserResult, isLoading, isError } = useAdminUser();
  const { data: meResult } = useMe();
  const meData = meResult?.data;
  const adminUserData = adminUserResult?.data;

  const isEditingOwnProfile = Boolean(adminUserData?.id === meData?.id);

  useEffect(() => {
    if (!id || !isNumber(id)) {
      navigate(AppRoutes.adminUser, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id && isNumber(id) && !isLoading && (isError || !adminUserData)) {
      navigate(AppRoutes.adminUser, { replace: true });
    }
  }, [id, isLoading, isError, adminUserData, navigate]);

  if (isLoading || !adminUserData) return <LoadingPage />;

  return <AdminUserEditForm key={id} adminUserData={adminUserData} isEditingOwnProfile={isEditingOwnProfile} />;
};
const AdminUserEditWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
`;
